/**
 * Profil — kim olduğun, ne yapabildiğin, ve hesap işlemleri.
 *
 * ─── NEDEN İZİNLER GÖSTERİLİYOR ──────────────────────────────────────────────
 * Kullanıcı "bunu neden yapamıyorum?" diye sorduğunda cevabı kendisi
 * görebilmeli. Eski sistemde yetki görünmezdi ve her soru destek talebine
 * dönüşürdü. Liste sunucudan geliyor — ekranda hesaplanmıyor.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { passwordChangeSchema, type PasswordChangeBody } from '@gasco/shared';

import { Badge } from '../design/Status.tsx';
import { Button } from '../design/Button.tsx';
import { Card, CardBody, CardHeader } from '../design/Card.tsx';
import { Field, Input, Select } from '../design/Field.tsx';
import { PageBody, PageHeader } from '../shell/AppShell.tsx';
import { SkeletonList } from '../design/Feedback.tsx';
import { TotpBolumu } from './TotpSection.tsx';
import { istek } from '../lib/api.ts';
import { useLogout, useSession, useSessionRefresh } from './session.ts';

export function ProfilePage(): React.JSX.Element {
  const { data: session, isPending } = useSession();
  const cikis = useLogout();
  const navigate = useNavigate();

  if (isPending) {
    return (
      <>
        <PageHeader title="Profil" />
        <PageBody>
          <SkeletonList rows={3} />
        </PageBody>
      </>
    );
  }
  if (!session) return <></>;

  return (
    <>
      <PageHeader title="Profil" />
      <PageBody>
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <div>
                <p className="font-medium text-ink">Kullanıcı #{session.userId}</p>
                <p className="text-sm text-ink-muted">Firma #{session.sessionFirmId}</p>
              </div>
              {session.isOperator && <Badge tone="ok">Operatör</Badge>}
            </CardHeader>
          </Card>

          {session.isOperator && <FirmaSecici />}

          <IzinListesi izinler={session.permissions} />

          <TotpBolumu />

          <ParolaDegistir />

          <Button
            variant="secondary"
            size="lg"
            block
            onClick={() => {
              void (async () => {
                await cikis();
                await navigate({ to: '/giris' });
              })();
            }}
          >
            Çıkış yap
          </Button>
        </div>
      </PageBody>
    </>
  );
}

function IzinListesi({ izinler }: { readonly izinler: readonly string[] }): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <p className="font-medium text-ink">Yetkileriniz</p>
        <span className="tabular text-sm text-ink-muted">{izinler.length}</span>
      </CardHeader>
      <CardBody>
        {izinler.length === 0 ? (
          <p className="text-sm text-ink-muted">
            Henüz hiçbir yetkiniz yok. Firma yöneticinizle görüşün.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {izinler.map((i) => (
              <li key={i}>
                <Badge>{i}</Badge>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Operatörün etkin firma seçimi.
 *
 * "Tümü" seçildiğinde operatör TÜM firmaları okur ama HİÇBİRİNE YAZAMAZ
 * [K-010]. Bu, ekranda da açıkça söylenmeli — yoksa kullanıcı kaydet
 * düğmesinin neden çalışmadığını anlamaz.
 */
function FirmaSecici(): React.JSX.Element {
  const { data: session } = useSession();
  const yenile = useSessionRefresh();
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const degistir = (deger: string): void => {
    void (async () => {
      setKaydediliyor(true);
      try {
        await istek('/auth/active-firm', {
          method: 'POST',
          body: { firmId: deger === '' ? null : Number(deger) },
        });
        await yenile();
      } finally {
        setKaydediliyor(false);
      }
    })();
  };

  return (
    <Card>
      <CardHeader>
        <p className="font-medium text-ink">Etkin firma</p>
      </CardHeader>
      <CardBody>
        <Field
          label="Firma"
          hint={
            session?.activeFirmId === null
              ? 'Tümü seçili: tüm firmaları görürsünüz ama hiçbirine yazamazsınız.'
              : 'Yazma işlemleri bu firmaya yapılır.'
          }
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={session?.activeFirmId === null ? '' : String(session?.activeFirmId)}
              disabled={kaydediliyor}
              onChange={(e) => degistir(e.target.value)}
            >
              <option value="">Tümü (salt okuma)</option>
              {session?.visibleFirms.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.code}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </CardBody>
    </Card>
  );
}

/**
 * Sonuç AYRIK BİRLİK — `'ok' | string` yazmıştım, ama `'ok'` zaten `string`'in
 * içinde eriyor ve tip hiçbir şey ayırt etmiyordu. Ayrık birlik, "başarı" ile
 * "hata mesajı"nı derleyici düzeyinde ayırır.
 */
type IslemSonucu = { readonly tur: 'basarili' } | { readonly tur: 'hata'; readonly mesaj: string };

function ParolaDegistir(): React.JSX.Element {
  const [sonuc, setSonuc] = useState<IslemSonucu | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PasswordChangeBody>({ resolver: zodResolver(passwordChangeSchema) });

  const gonder = handleSubmit(async (degerler) => {
    setSonuc(null);
    try {
      await istek('/auth/password', { method: 'POST', body: degerler });
      // Sunucu parola değişiminde OTURUMU DÜŞÜRÜR — kullanıcı bunu bilmeli.
      setSonuc({ tur: 'basarili' });
      reset();
    } catch (error) {
      setSonuc({
        tur: 'hata',
        mesaj: error instanceof Error ? error.message : 'Parola değiştirilemedi.',
      });
    }
  });

  return (
    <Card>
      <CardHeader>
        <p className="font-medium text-ink">Parola değiştir</p>
      </CardHeader>
      <CardBody>
        <form onSubmit={(e) => void gonder(e)} noValidate className="flex flex-col gap-3">
          <Field label="Mevcut parola" required error={errors.currentPassword?.message}>
            {({ id, required, describedBy, invalid }) => (
              <Input
                id={id}
                required={required}
                aria-describedby={describedBy}
                invalid={invalid}
                type="password"
                autoComplete="current-password"
                {...register('currentPassword')}
              />
            )}
          </Field>
          <Field
            label="Yeni parola"
            required
            hint="En az 12 karakter"
            error={errors.newPassword?.message}
          >
            {({ id, required, describedBy, invalid }) => (
              <Input
                id={id}
                required={required}
                aria-describedby={describedBy}
                invalid={invalid}
                type="password"
                autoComplete="new-password"
                {...register('newPassword')}
              />
            )}
          </Field>

          {sonuc?.tur === 'basarili' && (
            <p role="status" className="text-sm text-ok">
              Parolanız değişti. Güvenlik için diğer oturumlarınız kapatıldı.
            </p>
          )}
          {sonuc?.tur === 'hata' && (
            <p role="alert" className="text-sm text-danger">
              {sonuc.mesaj}
            </p>
          )}

          <Button type="submit" loading={isSubmitting}>
            Parolayı değiştir
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
